/**
 * Template Customization Modal
 * Allows users to customize system template colors and logos
 *
 * @file src/components/email/templates/TemplateCustomizationModal.tsx
 * @created 2025-10-22
 * @updated 2025-10-22 - Fully implemented with color pickers, logo selector, and preview
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import EmailService, { EmailTemplate } from '@/api/email';
import { Loader2, Palette, Image as ImageIcon, AlertCircle } from 'lucide-react';
import TemplateColorPicker from './TemplateColorPicker';
import TemplateLogoSelector from './TemplateLogoSelector';
import TemplatePreview from './TemplatePreview';

interface TemplateCustomizationModalProps {
  open: boolean;
  onClose: () => void;
  template: EmailTemplate;
  organisationId: string;
  restaurantId?: string;
  onComplete: (designJson: Record<string, any>) => void;
}

interface TemplateVariableValues {
  primaryColor: string;
  secondaryColor: string;
  primaryTextColor: string;
  secondaryTextColor: string;
  headerLogo: string | null;
  footerLogo: string | null;
}

/**
 * TemplateCustomizationModal Component
 *
 * Allows users to customize system templates by:
 * - Selecting brand colors (primary and secondary)
 * - Choosing a logo from their media library
 * - Previewing changes in real-time
 *
 * The customized template is then applied via the backend and returned
 * as a ready-to-use design_json for GrapesJS.
 *
 * @example
 * <TemplateCustomizationModal
 *   open={showModal}
 *   onClose={() => setShowModal(false)}
 *   template={selectedTemplate}
 *   organisationId="org-123"
 *   onComplete={(designJson) => editor.loadProjectData(designJson)}
 * />
 */
const TemplateCustomizationModal: React.FC<TemplateCustomizationModalProps> = ({
  open,
  onClose,
  template,
  organisationId,
  restaurantId,
  onComplete
}) => {
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [variables, setVariables] = useState<TemplateVariableValues>({
    primaryColor: '#c5b068',
    secondaryColor: '#890c17',
    primaryTextColor: '#ffffff',
    secondaryTextColor: '#000000',
    headerLogo: null,
    footerLogo: null
  });

  useEffect(() => {
    if (open) {
      fetchDefaultVariables();
    }
  }, [open, organisationId, restaurantId]);

  const fetchDefaultVariables = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[TemplateCustomizationModal] Fetching defaults with:', { organisationId, restaurantId });
      const defaults = await EmailService.getTemplateDefaults(organisationId, restaurantId || null);
      console.log('[TemplateCustomizationModal] Received defaults:', defaults);

      setVariables({
        primaryColor: defaults.primaryColor,
        secondaryColor: defaults.secondaryColor,
        primaryTextColor: defaults.primaryTextColor || '#ffffff',
        secondaryTextColor: defaults.secondaryTextColor || '#000000',
        headerLogo: defaults.headerLogo,
        footerLogo: defaults.footerLogo
      });
    } catch (error: any) {
      console.error('Error fetching default variables:', error);
      setError('Failed to load restaurant colors');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyTemplate = async () => {
    try {
      setApplying(true);
      setError(null);

      // Call backend to apply variables to template
      const response = await EmailService.customizeTemplate(template.id, variables);

      // Return customized design_json to parent
      onComplete(response.design_json);

    } catch (error: any) {
      console.error('Error applying template:', error);
      setError(error.message || 'Failed to customize template');
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
              <p className="text-gray-600">Loading customization options...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            Customize Template: {template.template_name}
          </DialogTitle>
          <p className="text-sm text-gray-600">
            Adjust colors and logo to match your brand
          </p>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex-1 grid grid-cols-2 gap-6 overflow-hidden">
          {/* Left: Customization Controls */}
          <div className="space-y-6 overflow-y-auto pr-4">
            {/* Colors Section - PLACEHOLDER */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Palette className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-lg">Brand Colors</h3>
                </div>

                <div className="space-y-4">
                  <TemplateColorPicker
                    label="Primary Color"
                    description="Used for headers, buttons, and main brand elements"
                    value={variables.primaryColor}
                    onChange={(color) => setVariables({ ...variables, primaryColor: color })}
                  />

                  <Separator />

                  <TemplateColorPicker
                    label="Secondary Color"
                    description="Used for accents, highlights, and secondary elements"
                    value={variables.secondaryColor}
                    onChange={(color) => setVariables({ ...variables, secondaryColor: color })}
                  />

                  <Separator />

                  <TemplateColorPicker
                    label="Primary Text Color"
                    description="Text color on primary color backgrounds (usually white)"
                    value={variables.primaryTextColor}
                    onChange={(color) => setVariables({ ...variables, primaryTextColor: color })}
                  />

                  <Separator />

                  <TemplateColorPicker
                    label="Secondary Text Color"
                    description="Text color on secondary color backgrounds"
                    value={variables.secondaryTextColor}
                    onChange={(color) => setVariables({ ...variables, secondaryTextColor: color })}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Logo Section */}
            {template.template_variables?.logos && (
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <ImageIcon className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-lg">Logo</h3>
                  </div>

                  <TemplateLogoSelector
                    organisationId={organisationId}
                    restaurantId={restaurantId}
                    selectedLogoUrl={variables.headerLogo}
                    onLogoSelect={(url) => setVariables({ ...variables, headerLogo: url, footerLogo: url })}
                  />
                </CardContent>
              </Card>
            )}

            {/* Tips */}
            <div className="p-4 bg-blue-50 rounded-md border border-blue-200">
              <div className="flex items-start gap-2">
                <div className="text-blue-500 mt-0.5">💡</div>
                <div>
                  <div className="font-medium text-sm text-blue-700">
                    Customization Tips
                  </div>
                  <ul className="text-xs text-blue-600 mt-1 space-y-1">
                    <li>• Colors are applied to all template elements</li>
                    <li>• You can further customize after inserting</li>
                    <li>• Changes preview in real-time on the right</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Live Preview */}
          <div className="bg-gray-50 rounded-lg p-4 overflow-y-auto">
            <TemplatePreview
              template={template}
              variables={variables}
              debounceMs={300}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={applying}>
            Cancel
          </Button>
          <Button onClick={handleApplyTemplate} disabled={applying}>
            {applying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Applying...
              </>
            ) : (
              'Use This Template'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateCustomizationModal;
